import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { matchesQuery } from "@/lib/picker-options";
import { cn } from "@/lib/utils";

export interface SearchSelectOption {
  value: string;
  label: string;
  /** Where it sits, e.g. a department's parent path. Shown under the label and beside it in the trigger. */
  context?: string;
  /** Shown in the list only, e.g. "Includes sub-units". */
  note?: string;
  /** Extra words to match, e.g. a site code. */
  keywords?: string;
}

export interface SearchSelectProps {
  /** Accessible name of the trigger, e.g. "Department". */
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: SearchSelectOption[];
  /** The "everything" or "nothing" choice listed first, e.g. All departments or — Unassigned —. */
  emptyOption: { value: string; label: string };
  searchPlaceholder?: string;
  /** Trigger width and height. */
  className?: string;
  /** Set by `<Field>` so its label names this control. */
  id?: string;
  "aria-describedby"?: string;
}


/**
 * Pick one item from a list the page has already loaded, by typing
 * (FEAT-2026-028, KPTM UAT MON-02).
 *
 * Replaces a `<Select>` for sites and departments: KPTM's 250 departments made
 * one long scroll of "KPTM › Alor Setar › …" paths. Typing matches any part of
 * the label or its context, and the options keep the order they came in, so a
 * department tree still reads top-down. For a list the page can't hold, such
 * as staff, use `EmployeePicker`, which searches the server.
 */
export function SearchSelect({
  label,
  value,
  onChange,
  options,
  emptyOption,
  searchPlaceholder = "Type to search…",
  className,
  id,
  "aria-describedby": describedBy,
}: SearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = value === emptyOption.value ? undefined : options.find((o) => o.value === value);
  const searching = query.trim().length > 0;
  const shown = useMemo(() => options.filter((o) => matchesQuery(o, query)), [options, query]);

  const pick = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  return (
    // Modal, so the list scrolls and takes focus inside an open Dialog.
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }} modal>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={label}
          aria-describedby={describedBy}
          title={selected ? [selected.context, selected.label].filter(Boolean).join(" › ") : undefined}
          // `flex`, not the button's inline-flex: like the `<Select>` trigger it
          // replaces, it sits on its own line under the filter's label.
          className={cn("flex w-full justify-between px-3 font-normal", className)}
        >
          <span className="truncate">
            {selected ? (
              <>
                {selected.label}
                {selected.context && <span className="text-muted-foreground"> · {selected.context}</span>}
              </>
            ) : (
              emptyOption.label
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(26rem,calc(100vw-2rem))] min-w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput placeholder={searchPlaceholder} value={query} onValueChange={setQuery} />
          <CommandList>
            <CommandGroup>
              {!searching && (
                <CommandItem value={`__empty__${emptyOption.value}`} onSelect={() => pick(emptyOption.value)}>
                  <Check className={cn("mr-2 h-4 w-4 shrink-0", selected ? "opacity-0" : "opacity-100")} />
                  {emptyOption.label}
                </CommandItem>
              )}
              {shown.map((o) => (
                <CommandItem key={o.value} value={o.value} onSelect={() => pick(o.value)} className="items-start">
                  <Check className={cn("mr-2 mt-0.5 h-4 w-4 shrink-0", o.value === value ? "opacity-100" : "opacity-0")} />
                  <span className="min-w-0">
                    <span className="block">{o.label}</span>
                    {(o.context || o.note) && (
                      // Faded from the row's own colour, not muted grey, so it stays
                      // readable on the highlighted row's accent background.
                      <span className="block text-xs opacity-70">
                        {[o.context, o.note].filter(Boolean).join(" · ")}
                      </span>
                    )}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
            {searching && shown.length === 0 && (
              <div className="py-3 text-center text-sm text-muted-foreground">{`Nothing matches "${query.trim()}".`}</div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
